import { Controller } from "@hotwired/stimulus"

const MIN_HOLD_MS = 6000

const PLANES = [
  { share: .42, depth: .1, size: .45, opacity: .2 },
  { share: .3, depth: .32, size: .75, opacity: .35 },
  { share: .2, depth: .68, size: 1.2, opacity: .5 },
  { share: .08, depth: 1.25, size: 2, opacity: .7 }
]

export default class extends Controller {
  static targets = ["canvas"]
  static values = { destination: String }

  connect() {
    this.finished = false
    this.element.classList.remove("first-light--recede", "first-light--leaving")
    this.ctx = this.canvasTarget.getContext("2d")
    this.reduced = matchMedia("(prefers-reduced-motion: reduce)").matches
    this.pointer = { x: 0, y: 0 }
    this.eased = { x: 0, y: 0 }
    this.startedAt = performance.now()
    this.recedeAt = MIN_HOLD_MS - 1400
    this.leaveAt = this.reduced ? MIN_HOLD_MS : MIN_HOLD_MS - 900
    this.enterAt = this.reduced ? MIN_HOLD_MS + 400 : MIN_HOLD_MS
    this.element.style.setProperty('--first-light-countdown', `${this.recedeAt}ms`)
    this.onResize = () => this.resize()
    this.onPointer = event => {
      this.pointer.x = event.clientX / innerWidth * 2 - 1
      this.pointer.y = event.clientY / innerHeight * 2 - 1
    }
    this.onVisibility = () => {
      if (document.hidden) this.stop()
      else { this.advance(); this.start() }
    }
    window.addEventListener("resize", this.onResize)
    window.addEventListener("pointermove", this.onPointer, { passive: true })
    document.addEventListener("visibilitychange", this.onVisibility)
    this.resize()
    if (!this.reduced) this.start()
    this.advance()
  }

  advance() {
    clearTimeout(this.timer)
    if (this.finished || document.hidden) return
    const elapsed = performance.now() - this.startedAt
    if (!this.reduced && elapsed >= this.recedeAt) this.element.classList.add("first-light--recede")
    if (elapsed >= this.leaveAt) this.element.classList.add("first-light--leaving")
    if (elapsed >= this.enterAt) {
      this.enter()
      location.assign(this.destinationValue)
    } else {
      // Timers may fire just before their deadline; never shorten the hold.
      const next = [this.recedeAt, this.leaveAt, this.enterAt].find(time => time > elapsed)
      this.timer = setTimeout(() => this.advance(), next - elapsed)
    }
  }

  disconnect() {
    this.stop()
    clearTimeout(this.timer)
    window.removeEventListener("resize", this.onResize)
    window.removeEventListener("pointermove", this.onPointer)
    document.removeEventListener("visibilitychange", this.onVisibility)
  }

  enter() {
    this.finished = true
    clearTimeout(this.timer)
    this.stop()
  }

  resize() {
    if (!this.ctx) return
    const canvas = this.canvasTarget
    this.width = canvas.clientWidth
    this.height = canvas.clientHeight
    const scale = Math.min(devicePixelRatio || 1, 2)
    canvas.width = Math.round(this.width * scale)
    canvas.height = Math.round(this.height * scale)
    this.ctx.setTransform(scale, 0, 0, scale, 0, 0)
    const count = Math.max(90, Math.min(320, Math.round(this.width * this.height / 5200)))
    this.stars = Array.from({ length: count }, () => {
      const roll = Math.random()
      let cumulative = 0
      const plane = PLANES.find(candidate => (cumulative += candidate.share) >= roll) || PLANES[0]
      return { x: Math.random() * this.width, y: Math.random() * this.height,
        radius: plane.size * (.7 + Math.random() * .6), alpha: plane.opacity * (.7 + Math.random() * .6),
        depth: plane.depth, phase: Math.random() * Math.PI * 2 }
    }).sort((a, b) => a.depth - b.depth)
    this.paint(0)
  }

  start() {
    if (this.finished || this.reduced || !this.ctx || this.frameId || document.hidden) return
    this.frameId = requestAnimationFrame(now => this.frame(now))
  }

  stop() {
    cancelAnimationFrame(this.frameId)
    this.frameId = null
  }

  frame(now) {
    this.paint((now - this.startedAt) / 1000)
    this.frameId = requestAnimationFrame(time => this.frame(time))
  }

  paint(seconds) {
    if (!this.ctx || !this.stars) return
    const context = this.ctx
    context.clearRect(0, 0, this.width, this.height)
    this.eased.x += (this.pointer.x - this.eased.x) * .08
    this.eased.y += (this.pointer.y - this.eased.y) * .08
    this.stars.forEach(star => {
      const x = star.x + (this.reduced ? 0 : this.eased.x * 54 * star.depth + Math.sin(seconds * .25 + star.phase) * 2 * star.depth)
      const y = star.y + (this.reduced ? 0 : this.eased.y * 38 * star.depth + Math.cos(seconds * .2 + star.phase) * 2 * star.depth)
      const alpha = star.alpha * (this.reduced ? 1 : .8 + .2 * Math.sin(seconds + star.phase))
      context.fillStyle = `rgba(224,235,255,${alpha})`
      context.beginPath()
      context.arc(x, y, star.radius, 0, Math.PI * 2)
      context.fill()
    })
  }
}
